import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Box,
  LinearProgress,
  Alert,
} from '@mui/material';
import { collection, doc, getDocs, getDoc, query, where } from 'firebase/firestore';
import { db } from '../../../../firebase';
import ContactListStep from './SendEventSteps/ContactListStep';
import EventSelectionStep, { EventData } from './SendEventSteps/EventSelectionStep';
import EmailTemplateStep from './SendEventSteps/EmailTemplateStep';
import ReviewStep from './SendEventSteps/ReviewStep';
import { formatDate } from '../../../../utils/dateUtils';

// Interface for selected contacts
interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  title?: string;
  tags: string[];
}

// Interface for email template
interface EmailTemplate {
  subject: string;
  body: string;
  includeCalendarInvite: boolean;
  includeTripDetails: boolean;
}

// Define the Event interface that matches EmailTemplateStep expectations
interface Event {
  id?: string;
  eventName: string;
  eventDate: string;
  eventDescription?: string;
  location?: string;
  startTime?: string;
  endTime?: string;
  isVirtual?: boolean;
  meetingLink?: string;
}

// Props interface
interface SendEventDialogProps {
  open: boolean;
  onClose: () => void;
  contactsToInvite: string[];
  organizationId: string;
}

const SendEventDialog: React.FC<SendEventDialogProps> = ({
  open,
  onClose,
  contactsToInvite,
  organizationId,
}) => {
  // State management
  const [activeStep, setActiveStep] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [events, setEvents] = useState<EventData[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<EventData | null>(null);
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  const [contactsData, setContactsData] = useState<Contact[]>([]);
  const [emailTemplate, setEmailTemplate] = useState<EmailTemplate>({
    subject: '',
    body: '',
    includeCalendarInvite: true,
    includeTripDetails: false,
  });
  const [sendingStatus, setSendingStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [sendingProgress, setSendingProgress] = useState<number>(0);
  const [isSingleContact, setIsSingleContact] = useState<boolean>(false);

  // Steps for the stepper - dynamic based on whether it's a single contact or multiple
  const steps = isSingleContact 
    ? ['Select Event', 'Customize Email', 'Review & Send'] 
    : ['Select Contacts', 'Select Event', 'Customize Email', 'Review & Send'];

  // Fetch available events on component mount
  useEffect(() => {
    if (open && organizationId) {
      fetchEvents();
      
      if (contactsToInvite.length > 0) {
        fetchSelectedContacts();
        
        // If only one contact is preselected, we're in single-contact mode
        if (contactsToInvite.length === 1) {
          setIsSingleContact(true);
          // Skip to event selection for single contact
          setActiveStep(0);
        }
      }
    }
  }, [open, organizationId, contactsToInvite]);

  // Fetch events from Firestore
  const fetchEvents = async () => {
    setLoading(true);
    try {
       // First, log the organizationId we're using to find events

       // Based on the event listing page, fetch from root events collection
       const eventsRef = collection(db, 'events');

       // Now try to get events specifically for this organization
       const q = query(eventsRef, where('organizationId', '==', organizationId));
       const orgEventsSnapshot = await getDocs(q);

       // If no events found, let's try an alternative field name
       if (orgEventsSnapshot.docs.length === 0) {
          const altQuery = query(eventsRef, where('orgId', '==', organizationId));
          const altOrgEventsSnapshot = await getDocs(altQuery);

          if (altOrgEventsSnapshot.docs.length > 0) {
             // Use data from altOrgEventsSnapshot
             const eventsData = processEventsData(altOrgEventsSnapshot.docs);
             setEvents(eventsData);
          } else {
             // Try getting all events, remove organization filtering temporarily for testing

             const allEvents = await getDocs(eventsRef);

             if (allEvents.docs.length > 0) {
                const allEventsData = processEventsData(allEvents.docs);

                setEvents(allEventsData);
             } else {
                setEvents([]);
             }
          }
       } else {
          // Use data from orgEventsSnapshot
          const eventsData = processEventsData(orgEventsSnapshot.docs);
          setEvents(eventsData);
       }
    } catch (error) {
      console.error('Error fetching events:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to process event data from Firestore
  const processEventsData = (docs: any[]): EventData[] => {
    return docs.map(doc => {
      const data = doc.data();
      // Map raw Firestore data to our EventData interface structure
      return {
        id: doc.id,
        // Support various field names and provide fallbacks
        eventName: data.eventName || data.name || data.title || data.event_name || 'Unnamed Event',
        eventDate: data.eventDate || data.date || data.startDate || data.start_date || data.event_date || '',
        eventDescription: data.eventDescription || data.description || data.desc || '',
        location: data.location || data.venue || data.place || '',
        startTime: data.startTime || data.start_time || '',
        endTime: data.endTime || data.end_time || '',
        isVirtual: data.isVirtual || data.is_virtual || data.virtual || false,
        meetingLink: data.meetingLink || data.meeting_link || data.link || '',
        // Keep original data for reference
        ...data
      };
    });
  };

  // Fetch selected contacts details
  const fetchSelectedContacts = async () => {
    setLoading(true);
    try {
      const contacts: Contact[] = [];
      
      for (const contactId of contactsToInvite) {
        const contactRef = doc(db, `organizations/${organizationId}/contacts/${contactId}`);
        const contactSnap = await getDoc(contactRef);
        
        if (contactSnap.exists()) {
          const data = contactSnap.data();
          contacts.push({
            id: contactSnap.id,
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            email: data.email || '',
            phone: data.phone || '',
            company: data.company || '',
            title: data.title || '',
            tags: Array.isArray(data.tags) ? data.tags : [],
          });
        }
      }
      
      setContactsData(contacts);
      setSelectedContactIds(contactsToInvite);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get Contact objects from IDs
  const getSelectedContactsObjects = (): Contact[] => {
    return contactsData.filter(contact => selectedContactIds.includes(contact.id));
  };

  // Handle next step
  const handleNext = () => {
    // Validate current step
    if (activeStep === 0 && !selectedEvent) {
      // Event selection step - require event selection
      return;
    }
    
    if (activeStep === (isSingleContact ? 1 : 2) && (!emailTemplate.subject || !emailTemplate.body)) {
      // Email template step - require subject and body
      return;
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  // Handle back step
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Handle event selection
  const handleEventSelect = (event: EventData) => {
    setSelectedEvent(event);
    // If we already have an email template setup, update it with the new event info
    if (emailTemplate.subject || emailTemplate.body) {
      updateEmailTemplateWithEvent(event);
    } else {
      // Pre-populate email template based on selected event
      setEmailTemplate({
        subject: `Invitation: ${event.eventName}`,
        body: `
Dear {{contactName}},

You are cordially invited to attend ${event.eventName} on {{eventDate}}.

Event Details:
- Date: {{eventDate}}
- Time: ${event.startTime || '(TBD)'} - ${event.endTime || '(TBD)'}
- Location: ${event.location || '(TBD)'}
${event.isVirtual ? `- Meeting Link: ${event.meetingLink || '(will be provided closer to the event)'}` : ''}

Description:
${event.eventDescription || 'Join us for this exciting event!'}

We hope to see you there!

Best regards,
{{organizationName}} Team
      `,
        includeCalendarInvite: true,
        includeTripDetails: false,
      });
    }
  };

  // Update email template when event changes
  const updateEmailTemplateWithEvent = (event: EventData) => {
    const eventName = event.eventName || event.name || event.title || '';
    
    // Convert Firebase Timestamp to string if necessary
    const eventDateValue = event.eventDate || event.date || event.startDate || '';
    const eventDate = typeof eventDateValue === 'object' && eventDateValue !== null && 'seconds' in eventDateValue
      ? new Date(eventDateValue.seconds * 1000).toISOString()
      : eventDateValue;
    
    setEmailTemplate(prev => {
      // Only update if we already have content
      if (!prev.subject && !prev.body) return prev;
      
      return {
        ...prev,
        subject: prev.subject.replace(/\{\{eventName\}\}/g, eventName),
        body: prev.body
          .replace(/\{\{eventName\}\}/g, eventName)
          .replace(/\{\{eventDate\}\}/g, formatDate(eventDate))
      };
    });
  };

  // Handle template changes
  const handleTemplateChange = (template: EmailTemplate) => {
    setEmailTemplate(template);
  };

  // Handle send invitations
  const handleSendInvitations = async () => {
    if (!selectedEvent) return;
    
    setSendingStatus('sending');
    
    try {
       // This would be implemented to actually send emails
       // For now, just simulate a delay and success
       await new Promise((resolve) => setTimeout(resolve, 2000));

       setSendingStatus('success');
    } catch (error) {
      console.error('Error sending invitations:', error);
      setSendingStatus('error');
    }
  };

  // Format date for display
  const formatDate = (dateString: string | Date): string => {
    if (!dateString) return '';
    try {
      const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString.toString();
    }
  };

  // Utility function to convert EventData to Event
  const convertEventDataToEvent = (eventData: EventData | null): Event | null => {
    if (!eventData) return null;
    
    // Convert eventDate to string if it's a Firestore timestamp
    const eventDate = typeof eventData.eventDate === 'object' 
      ? formatDate(new Date((eventData.eventDate as { seconds: number }).seconds * 1000))
      : formatDate(eventData.eventDate);
    
    return {
      id: eventData.id,
      eventName: eventData.eventName || eventData.name || eventData.title || eventData.event_name || '',
      eventDate: eventDate,
      eventDescription: eventData.eventDescription || eventData.description || eventData.desc || '',
      location: eventData.location || eventData.venue || eventData.place || '',
      startTime: typeof eventData.startTime === 'object' 
        ? new Date((eventData.startTime as { seconds: number }).seconds * 1000).toLocaleTimeString() 
        : eventData.startTime,
      endTime: typeof eventData.endTime === 'object' 
        ? new Date((eventData.endTime as { seconds: number }).seconds * 1000).toLocaleTimeString() 
        : eventData.endTime,
      isVirtual: eventData.isVirtual,
      meetingLink: eventData.meetingLink
    };
  };

  // Render step content based on active step
  const getStepContent = (step: number) => {
    // For single contact mode, adjust the step numbers
    // In single contact mode: 0=Event Selection, 1=Email Template, 2=Review
    // In multi contact mode: 0=Contact Selection, 1=Event Selection, 2=Email Template, 3=Review
    const adjustedStep = isSingleContact ? step : step - 1;
    
    switch (adjustedStep) {
      case -1: // Only in multi-contact mode (step 0)
        return (
          <ContactListStep 
            contacts={contactsData}
            selectedContacts={selectedContactIds}
            onContactsChange={setSelectedContactIds}
            loading={loading}
          />
        );
      case 0: // Event selection (step 0 in single-contact mode, step 1 in multi-contact mode)
        return (
          <EventSelectionStep
            events={events || []} // Ensure events is never undefined
            selectedEvent={selectedEvent}
            onSelectEvent={handleEventSelect}
            loading={loading}
          />
        );
      case 1: // Email template (step 1 in single-contact mode, step 2 in multi-contact mode)
        return (
          <EmailTemplateStep
            template={emailTemplate}
            onChange={handleTemplateChange}
            selectedEvent={{
              id: selectedEvent?.id,
              eventName: selectedEvent?.eventName || '',
              eventDate: selectedEvent ? (
                typeof selectedEvent.eventDate === 'string' 
                  ? formatDate(selectedEvent.eventDate)
                  : formatDate(new Date((selectedEvent.eventDate as { seconds: number }).seconds * 1000))
              ) : '',
              eventDescription: selectedEvent?.eventDescription,
              location: selectedEvent?.location,
              startTime: typeof selectedEvent?.startTime === 'string'
                ? selectedEvent.startTime
                : selectedEvent?.startTime
                  ? new Date((selectedEvent.startTime as { seconds: number }).seconds * 1000).toLocaleTimeString()
                  : undefined,
              endTime: typeof selectedEvent?.endTime === 'string'
                ? selectedEvent.endTime
                : selectedEvent?.endTime
                  ? new Date((selectedEvent.endTime as { seconds: number }).seconds * 1000).toLocaleTimeString()
                  : undefined,
              isVirtual: selectedEvent?.isVirtual,
              meetingLink: selectedEvent?.meetingLink,
            } as Event}
            selectedContacts={getSelectedContactsObjects()}
          />
        );
      case 2: // Review (step 2 in single-contact mode, step 3 in multi-contact mode)
        return (
          <ReviewStep
            selectedEvent={convertEventDataToEvent(selectedEvent)}
            selectedContacts={getSelectedContactsObjects()}
            emailTemplate={emailTemplate}
            sendingStatus={sendingStatus}
            onSend={handleSendInvitations}
          />
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="send-event-dialog-title"
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {isSingleContact && selectedContactIds.length === 1 
              ? `Invite ${contactsData.find(contact => contact.id === selectedContactIds[0])?.firstName || ''} ${contactsData.find(contact => contact.id === selectedContactIds[0])?.lastName || ''} to Event` 
              : 'Invite Contacts to Event'}
          </Typography>
          {activeStep === (isSingleContact ? 0 : 1) && (
            <Alert severity="info">Please select an event to proceed</Alert>
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <Box sx={{ minHeight: 400 }}>
          {getStepContent(activeStep)}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={onClose}
          color="inherit"
          disabled={sendingStatus === 'sending'}
        >
          Cancel
        </Button>
        
        <Box sx={{ flex: '1 1 auto' }} />
        
        {activeStep > 0 && (
          <Button 
            onClick={handleBack}
            color="inherit"
            disabled={sendingStatus === 'sending'}
          >
            Back
          </Button>
        )}
        
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={
              (activeStep === 0 && !isSingleContact && selectedContactIds.length === 0) ||
              (activeStep === (isSingleContact ? 0 : 1) && !selectedEvent) ||
              loading
            }
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSendInvitations}
            disabled={sendingStatus === 'sending' || sendingStatus === 'success'}
          >
            {sendingStatus === 'sending' ? (
              <>
                <LinearProgress sx={{ width: 24, mr: 1 }} color="inherit" />
                Sending...
              </>
            ) : (
              sendingStatus === 'success' ? 'Sent!' : 'Send Invitations'
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SendEventDialog;
